import {Injectable} from '@angular/core';
import {Observable, combineLatest} from 'rxjs';
import {StoreSelectorsService} from "./store-selectors.service";
import {IGroupCollection, IGroupCollectionDetails, PrivilegeModules, PrivilegeModulesProperties, PrivilegeModulesTable} from "../interfaces/enterprise-interface";
import {debounceTime, distinctUntilChanged, filter, map, take} from "rxjs/operators";
import {Lib} from "../../libs/Lib";
import {Compbaser} from "../components/compbaser";
import {compareIds, isNullOrUndefOrEmpty} from "../common";
import {actionLoadStationRemove, actionRemoveEventParam, actionRemoveMusicChannel, actionRemoveResources, actionRemoveScenes} from "../store/actions/user.actions";
import {IAccessTable, Id, IUser} from "../interfaces";
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";

@Injectable()
export class AccessService extends Compbaser {

    m_user: IUser;

    /** One-shot guard for the opt-in station filter subscription. The user-mask observable
     *  re-emits whenever accessMask changes, which would otherwise spawn a fresh
     *  combineLatest subscription inside removeStations() every time. Single-flip so the
     *  long-lived stream is created exactly once per AccessService lifetime. */
    private stationFilterSubscribed = false;

    constructor(private storeSelectorsService: StoreSelectorsService, private toastr: ToastrService, private translocoService: TranslocoService) {
        super();
        this.initAccountRestrictions();
    }

    private initAccountRestrictions() {
        this.cod(
            this.storeSelectorsService
                .getUserAccessMask()
                .subscribe((maskDecoded) => {
                    this.removeScenes(maskDecoded);
                    this.removeResources(maskDecoded);
                    this.removeMusic(maskDecoded);
                    this.removeEventParams(maskDecoded);
                    /** removeStations runs LAST on purpose — it reads the post-removeScenes store
                     *  so a station whose scene has just been filtered out is treated as "scene
                     *  not accessible to this sub-user" and the station gets hidden too. */
                    this.removeStations();
                })
        );
    }
    public getGroupsTable(i_module: PrivilegeModules, i_tableName: PrivilegeModulesTable): Observable<IAccessTable> {
        const fullAccess = {upd: true, add: true, del: true};
        return this.storeSelectorsService.getGroupsPrivileges()
            .pipe(
                map((v) => {
                    if (!v?.Group) {
                        /** possibly user did not login so we just default **/
                        return fullAccess;
                    }
                    const r = v.Group.findIndex((x) => x.$.name == i_module);
                    /** did not find the module as it must have been added recently (like 'SignStudio editors' for example that was added in 2022)
                     and so if we don't find the module we just give full access which is fine since this user is not under a reseller, since
                     if he was under a reseller, his reseller would have already created a new set of privileges which would have had ALL the latest modules / tables
                     **/
                    if (r === -1) {
                        // Lib.DevShow(`ACL: found new module ${i_module} allowing full access`);
                        return fullAccess;
                    }
                    /** Groups without a usable <Tables> node (e.g. attribute-only groups like
                     *  'SignStudio AI', or legacy XML variants) fail OPEN like a missing module —
                     *  previously this line hard-crashed the observable on Tables[0].$ **/
                    const mask = v.Group[r].Tables?.[0]?.$?.[i_tableName];
                    if (mask === undefined || mask === null) {
                        return fullAccess;
                    }
                    const a = Lib.GetAccessMask3bit(Number(mask));
                    if (isNullOrUndefOrEmpty(r)) {
                        Lib.DevShow('ACL: no privileges so defaulting to all access');
                        return fullAccess;
                    }
                    return {upd: a[0], add: a[1], del: a[2]};
                }));
    }

    /** accessService.getGroupsProperties("Stations", 'lanServer') or accessService.getGroupsProperties("Stations") **/
    public getGroupsProperties(i_module: PrivilegeModules, i_propertyName?: PrivilegeModulesProperties): Observable<string | IGroupCollectionDetails> {
        return this.storeSelectorsService.getGroupsPrivileges()
            .pipe(
                map((v) => {
                    /** module absent from the user's privilege XML (newly-added module on a
                     *  legacy XML) — previously v.Group[-1].$ hard-crashed the observable **/
                    const r = v?.Group?.findIndex((x) => x?.$?.name == i_module);
                    if (r === undefined || r === -1) {
                        return null;
                    }
                    if (i_propertyName) {
                        return v.Group[r].$[i_propertyName];
                    } else {
                        return v.Group[r].$;
                    }
                }));
    }

    public getGroupsCollection(i_module: PrivilegeModules): Observable<IGroupCollection> {
        return this.storeSelectorsService.getGroupsPrivileges()
            .pipe(
                debounceTime(100),
                distinctUntilChanged((a, b) => {
                    return JSON.stringify(a) === JSON.stringify(b);
                }),
                map((v) => {
                    if (isNullOrUndefOrEmpty(v)) {
                        console.log('getGroupsCollection: did not find group info');
                        return null;
                    }
                    const r = v?.Group?.findIndex((x) => x?.$?.name == i_module);
                    /** didn't find the group so must have been recently added XML module / new group **/
                    if (r === undefined || r === -1) {
                        return null;
                    }
                    return v.Group[r];
                }));
    }

    /** Default-ON privilege feature attribute reader (the runtime mirror of getAttributeGroup
     *  in Lib.ts, and the opposite of the opt-in filterByScene pattern). Used for feature
     *  toggles that 99% of customers never touch — e.g. the 'SignStudio AI' cherry-pick attrs.
     *
     *  Fail-open by design: missing privileges XML (free user / not under a reseller),
     *  missing group (legacy XML the reseller never re-saved) or missing attribute all mean
     *  ENABLED (getGroupsCollection emits null for those → undefined !== '0' → true). Only
     *  the literal attribute value '0' disables the feature. Delegating to
     *  getGroupsCollection also inherits its debounce + distinctUntilChanged dedupe.
     *
     *  NOTE getGroupsPrivileges may never emit for some account types (filter on null), so
     *  consumers MUST initialize their component flag to true and only flip it here.
     *  See src/app/containers/enterprise/docs/09-adding-privilege-feature-toggles.md */
    public isPrivilegeFeatureEnabled(i_module: PrivilegeModules, i_attribute: PrivilegeModulesProperties): Observable<boolean> {
        return this.getGroupsCollection(i_module)
            .pipe(
                map((group: any) => group?.$?.[i_attribute] !== '0'),
                distinctUntilChanged());
    }

    private removeMusic(maskTemplate: Array<string>) {
        this.storeSelectorsService.getMusicChannels()
            .pipe(take(1))
            .subscribe((items) => {
                items.forEach((s) => {
                    if (maskTemplate.findIndex((v) => compareIds(v, s.access_key)) == -1) {
                        this.consoleRemovedFromStore('music', s.music_channel_id);
                        this.storeSelectorsService.dispatch(actionRemoveMusicChannel({music_channel_id: s.music_channel_id}));
                    }
                });
            });
    }

    private removeResources(maskTemplate: Array<string>) {
        this.storeSelectorsService.getResources()
            .pipe(take(1))
            .subscribe((items) => {
                const ids = [];
                items.filter((s) => {
                    if (maskTemplate.findIndex((v) => compareIds(v, s.access_key)) == -1) {
                        this.consoleRemovedFromStore('resource', s.resource_name);
                        ids.push(s.resource_id);
                    }
                });
                this.storeSelectorsService.dispatch(actionRemoveResources({resourceIds: ids}));
            });
    }


    private removeEventParams(maskTemplate: Array<string>) {
        this.storeSelectorsService.getEventParams()
            .pipe(take(1))
            .subscribe((items) => {
                items.forEach((s) => {
                    if (maskTemplate.findIndex((v) => compareIds(v, s.access_key)) == -1) {
                        this.consoleRemovedFromStore('eventParam', s.event_param_id);
                        this.storeSelectorsService.dispatch(actionRemoveEventParam({eventParamId: s.event_param_id}));
                    }
                });
            });
    }

    private consoleRemovedFromStore(i_from: string, i_ids) {
        // if (Lib.DevMode()) {
        //      console.log(`access removal ${i_from} ${i_ids}`);
        // }
    }

    private removeScenes(maskTemplate: Array<string>) {
        this.storeSelectorsService.getScenes(true)
            .pipe(take(1))
            .subscribe((items) => {
                const ids = [];
                items.forEach((s) => {
                    if (maskTemplate.findIndex((v) => compareIds(v, s.Player.$.accessKey)) == -1) {
                        ids.push(s.Player.$.id);
                    }
                });
                this.consoleRemovedFromStore('scenes', ids);
                this.storeSelectorsService.dispatch(actionRemoveScenes({sceneIds: ids}));
            });
    }

    /** Optional scene-driven station filter. Triple-gated to be a complete no-op for the
     *  99.9% of customers who have never opted into the feature.
     *
     *  Inside gate 2 (opt-in customers only) the filter waits for BOTH stations and
     *  scenes to be non-empty arrays AND for 1 second of inactivity (debounceTime) so
     *  that all the other remove* methods have finished filtering scenes/resources/etc.
     *  Then it takes a ONE-SHOT snapshot and applies the filter exactly once. This is
     *  more conservative than a live combineLatest because every emission of the live
     *  stream is a potential window for partial/mid-load state to cause over-filtering.
     *
     *  Customers with the flag off never enter the post-gate-2 branch, so behavior is
     *  byte-identical for them.
     *
     *  Diagnostics: when the flag is on, key milestones are logged with the
     *  [filterByScene] prefix so an admin can paste console output to debug.
     *
     *  See src/app/containers/enterprise/docs/08-station-filter-by-scene.md */
    private removeStations() {
        /** Mask re-emits would otherwise re-enter this method and stack subscriptions. */
        if (this.stationFilterSubscribed) return;

        this.storeSelectorsService.getGroupsPrivileges()
            .pipe(take(1))
            .subscribe((privileges: any) => {
                /** GATE 1: privileges may be undefined (free user / plus standalone / not logged in).
                 *  getGroupsPrivileges has filter(v !== null) so the subscribe callback simply
                 *  never fires in those cases — but the optional chains here are belt-and-suspenders. */
                const playersGroup = privileges?.Group?.find((g: any) => g?.$?.name === 'SignStudio Players');
                if (!playersGroup) return;
                /** GATE 2: opt-in flag must be exactly '1'. Absent / undefined / '0' all leave stations untouched. */
                if (playersGroup?.$?.filterByScene !== '1') return;

                /** Beyond this line: opt-in customers only. */
                this.stationFilterSubscribed = true;
                console.log('[filterByScene] GATES PASSED — waiting for stations + scenes to settle');

                this.cod(
                    combineLatest([
                        this.storeSelectorsService.getStations(true),
                        this.storeSelectorsService.getScenes(true),
                    ]).pipe(
                        /** Don't act until BOTH arrays are populated. Empty scenes (which
                         *  would otherwise produce an empty accessibleSceneIds and purge
                         *  every station with a valid scene id) is treated as "not ready
                         *  yet" — wait for scenes to arrive. */
                        filter(([s, sc]) => Array.isArray(s) && s.length > 0
                                         && Array.isArray(sc) && sc.length > 0),
                        /** Wait for 1 second of no further emissions before snapshotting.
                         *  This catches the cascade: actionLoadAccountSuccess → removeScenes
                         *  dispatches → scenes re-emit filtered. By the end of debounce we
                         *  have the final post-removeScenes scene set. */
                        debounceTime(1000),
                        /** ONE-SHOT: apply the filter once and stop listening. No risk of
                         *  spurious mid-state re-evaluations. */
                        take(1),
                    ).subscribe(([stations, scenes]) => {
                        const accessibleSceneIds = new Set<string>();
                        (scenes ?? []).forEach((s) => {
                            const id = s?.Player?.$?.id;
                            if (id) accessibleSceneIds.add(String(id));
                        });
                        const accessibleIdsArr = Array.from(accessibleSceneIds).sort();
                        console.log(`[filterByScene] snapshot: stations=${stations.length} scenes=${scenes.length} accessibleSceneIds=[${accessibleIdsArr.join(',')}]`);

                        /** Final safety: if the snapshot yielded no accessible scene ids
                         *  (somehow scenes is populated but none has an id), don't filter —
                         *  keep all stations visible. */
                        if (accessibleSceneIds.size === 0) {
                            console.log('[filterByScene] no accessible scene ids — keeping all stations visible');
                            return;
                        }

                        const idsToRemove: Array<Id> = [];
                        stations.forEach((station) => {
                            const pdi = station?.player_data_id;
                            /** GATE 3 (per-station): no scene assigned yet → keep visible.
                             *  user.service.ts:1128 does `String(player_data_id)` on raw server
                             *  values, so when the server returns null/undefined/NaN for an
                             *  unassigned station, pdi becomes the LITERAL string
                             *  "null" / "undefined" / "NaN". All five sentinel forms below
                             *  mean "no scene assigned" — keep visible (lenient default). */
                            const pdiStr = pdi == null ? '' : String(pdi).trim();
                            if (!pdiStr
                                || pdiStr === '0'
                                || pdiStr === 'null'
                                || pdiStr === 'undefined'
                                || pdiStr === 'NaN') return;
                            if (!accessibleSceneIds.has(pdiStr)) {
                                idsToRemove.push(station.station_id);
                            }
                        });
                        console.log(`[filterByScene] total: keeping ${stations.length - idsToRemove.length} / removing ${idsToRemove.length}`);
                        if (idsToRemove.length > 0) {
                            this.consoleRemovedFromStore('stations', idsToRemove);
                            this.storeSelectorsService.dispatch(actionLoadStationRemove({stationIds: idsToRemove}));
                        }
                    })
                );
            });
    }

    public showNoAccess() {
        this.toastr.clear();
        return this.toastr.error(this.translocoService.translate('toast.noPermissionUpgEnterprise', {}, this.translocoService.getActiveLang()));
    }
}

